import { useStyletron } from '@thanx/uikit/theme'
import React, { useEffect, useRef } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import { Icon } from 'controlled-components'
import {
  clearBottomSelection,
  clearTopSelection,
  openTop,
  selectLevel2OperatorOptions,
  selectTopIsOpen,
  selectTopSearchTerm,
  selectTopSelectedItem,
  selectTopSelectedItemData,
  setBottomDisabled,
  // Bottom actions for cascading business logic
  setBottomOperator,
  setTopSearchTerm,
  toggleTopIsOpen,
} from 'slices/aiSegmentBuilder/picker'

// ═══════════════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════════════

interface OwnProps {
  pickerId: number
  placeholder?: string
  disabled?: boolean
  // Data fetching props passed from parent (single source of truth)
  search: (term: string) => void
  refetch: () => void
  isLoading: boolean
}

type Props = OwnProps & ConnectedProps<typeof connector>

// ═══════════════════════════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Container for the single-select dropdown trigger.
 * Builds the trigger UI directly using atomic components (Icon).
 * Connects to Redux for search, selection, and disabled state.
 */
const TriggerContainer: React.FC<Props> = ({
  pickerId,
  placeholder = 'Select item...',
  disabled = false,
  search,
  refetch,
  isLoading,
  isOpen,
  searchTerm,
  selectedItem,
  selectedItemData,
  bottomOperatorOptions,
  setSearchTerm,
  clearSelection,
  toggleIsOpen,
  open,
  setBottomOp,
  clearBottom,
  setBottomDis,
}) => {
  const [css, theme] = useStyletron()
  const inputRef = useRef<HTMLInputElement>(null)

  // Derived: selected item label
  const selectedLabel = selectedItemData?.name ?? ''

  // Derived: show clear button
  const showClear = selectedItem !== null && !disabled

  // Auto-focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  // Handlers
  const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled) return
    // Don't toggle if clicking on the input or buttons
    const target = e.target as HTMLElement
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'BUTTON' ||
      target.closest('button')
    )
      return

    if (!isOpen) {
      // Opening dropdown - refetch current data
      refetch()
    } else {
      // Closing dropdown - reset search term
      setSearchTerm({ pickerId, term: '' })
    }
    toggleIsOpen({ pickerId })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchTerm({ pickerId, term: value })
    search(value)
  }

  const handleInputFocus = () => {
    if (disabled) return
    if (!isOpen) {
      refetch()
      open({ pickerId })
    }
  }

  const handleClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    clearSelection({ pickerId })
    // Business logic: reset bottom state when top is cleared
    if (bottomOperatorOptions.length > 0) {
      setBottomOp({ pickerId, operator: bottomOperatorOptions[0].id })
    }
    clearBottom({ pickerId })
    setBottomDis({ pickerId, disabled: true })
  }

  // Styles
  const containerClassName = css(
    getContainerStyles(theme, { isOpen, disabled: disabled })
  )
  const searchIconClassName = css(getSearchIconStyles(theme))
  const inputClassName = css(getInputStyles(theme))
  const closedTextClassName = css(
    getClosedTextStyles(theme, { hasText: Boolean(selectedLabel) })
  )
  const chevronClassName = css(getChevronStyles(theme, { isOpen }))
  const iconsContainerClassName = css(getIconsContainerStyles())
  const iconButtonClassName = css(getIconButtonStyles(theme))
  const spinnerClassName = css(getSpinnerStyles(theme))

  // Display text when closed: show selectedLabel if provided, otherwise placeholder
  const closedDisplayText = selectedLabel || placeholder

  return (
    <div
      role="combobox"
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      onClick={handleContainerClick}
      className={containerClassName}
    >
      {/* Search icon */}
      <span className={searchIconClassName}>
        <Icon name="search" size={16} />
      </span>

      {/* When open: show search input. When closed: show selected item or placeholder */}
      {isOpen ? (
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className={inputClassName}
        />
      ) : (
        <span className={closedTextClassName}>{closedDisplayText}</span>
      )}

      {/* Icons container */}
      <div className={iconsContainerClassName}>
        {isLoading && (
          <span className={spinnerClassName}>
            <Icon name="spinner" size={14} spin />
          </span>
        )}
        {showClear && !isLoading && (
          <button
            type="button"
            onClick={handleClearClick}
            className={iconButtonClassName}
            aria-label="Clear selection"
          >
            <Icon name="x" size={14} />
          </button>
        )}
        {/* Caret */}
        <span className={chevronClassName}>
          <Icon name="caret-down" size={16} />
        </span>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Styles
// ═══════════════════════════════════════════════════════════════════════════

interface ContainerStyleParams {
  isOpen: boolean
  disabled: boolean
}

const getContainerStyles = (
  theme: any,
  { isOpen, disabled }: ContainerStyleParams
) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '8px 12px',
  height: '48px',
  boxSizing: 'border-box' as const,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: disabled
    ? theme.colors.grey30
    : isOpen
    ? theme.colors.primary400
    : theme.colors.grey30,
  borderRadius: theme.borders.radius200,
  backgroundColor: disabled ? theme.colors.grey10 : theme.colors.white,
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: 'border-color 150ms ease',
  outline:
    isOpen && !disabled ? `1px solid ${theme.colors.primary400}` : 'none',
  outlineOffset: '-1px',
  opacity: disabled ? 0.7 : 1,
})

const getSearchIconStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
  flexShrink: 0,
})

const getInputStyles = (theme: any) => ({
  flex: 1,
  border: 'none',
  outline: 'none',
  fontSize: '14px',
  lineHeight: '20px',
  padding: '0',
  color: theme.colors.grey90,
  backgroundColor: 'transparent',
  '::placeholder': {
    color: theme.colors.grey50,
  },
})

interface ClosedTextStyleParams {
  hasText: boolean
}

const getClosedTextStyles = (
  theme: any,
  { hasText }: ClosedTextStyleParams
) => ({
  flex: 1,
  fontSize: '14px',
  lineHeight: '20px',
  color: hasText ? theme.colors.grey90 : theme.colors.grey50,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
})

interface ChevronStyleParams {
  isOpen: boolean
}

const getChevronStyles = (theme: any, { isOpen }: ChevronStyleParams) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
  flexShrink: 0,
  transition: 'transform 150ms ease',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
})

const getIconsContainerStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  flexShrink: 0,
})

const getIconButtonStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '2px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: theme.colors.grey60,
  borderRadius: '2px',
})

const getSpinnerStyles = (theme: any) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.colors.grey50,
})

// ═══════════════════════════════════════════════════════════════════════════
// Redux Connection
// ═══════════════════════════════════════════════════════════════════════════

const mapStateToProps = (state: any, ownProps: OwnProps) => ({
  isOpen: selectTopIsOpen(state, ownProps.pickerId),
  searchTerm: selectTopSearchTerm(state, ownProps.pickerId),
  selectedItem: selectTopSelectedItem(state, ownProps.pickerId),
  selectedItemData: selectTopSelectedItemData(state, ownProps.pickerId),
  bottomOperatorOptions: selectLevel2OperatorOptions(state, ownProps.pickerId),
})

const mapDispatchToProps = {
  setSearchTerm: setTopSearchTerm,
  clearSelection: clearTopSelection,
  toggleIsOpen: toggleTopIsOpen,
  open: openTop,
  // Bottom actions for cascading business logic
  setBottomOp: setBottomOperator,
  clearBottom: clearBottomSelection,
  setBottomDis: setBottomDisabled,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default connector(TriggerContainer)
